<template>
  <button
    class="button is-primary"
    v-on:click="openModal = true"
    v-bind="$attrs"
    :disabled="!canCreate"
    :title="canCreate ? '' : 'You do not have the required permission to create a worker'"
  >
    Create
  </button>

  <Modal
    v-if="canCreate"
    v-model="openModal"
    title="Create a worker"
  >
    <form
      class="form has-text-left"
      v-on:submit.prevent="create"
    >
      <div class="field">
        <label class="label">Name</label>
        <div class="control">
          <input
            autofocus
            class="input"
            :class="{ 'is-danger': fieldErrors.name }"
            type="text"
            placeholder="…"
            maxlength="100"
            v-model.trim="payload.name"
          />
        </div>
        <p
          v-if="fieldErrors.name"
          class="help is-danger"
        >
          {{ fieldErrors.name.join(', ') }}
        </p>
      </div>

      <div class="field">
        <label class="label">Slug</label>
        <div class="control">
          <input
            class="input"
            :class="{ 'is-danger': fieldErrors.slug }"
            type="text"
            placeholder="…"
            maxlength="100"
            v-model.trim="payload.slug"
          />
        </div>
        <p
          v-if="fieldErrors.slug"
          class="help is-danger"
        >
          {{ fieldErrors.slug.join(', ') }}
        </p>
      </div>

      <div class="field">
        <label class="label">Type</label>
        <div class="control">
          <input
            class="input"
            :class="{ 'is-danger': fieldErrors.type }"
            type="text"
            placeholder="…"
            maxlength="100"
            v-model.trim="payload.type"
          />
        </div>
        <p
          v-if="fieldErrors.type"
          class="help is-danger"
        >
          {{ fieldErrors.type.join(', ') }}
        </p>
      </div>
    </form>

    <template v-slot:footer="{ close }">
      <button
        class="button"
        v-on:click="close"
      >
        Cancel
      </button>
      <button
        type="submit"
        v-on:click="create"
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        :disabled="!canSave"
        :title="saveTitle"
      >
        Create
      </button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { isAxiosError } from 'axios'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import { type CreateWorkerPayload, DuplicatedWorker } from '@/api'
import Modal from '@/components/Modal.vue'
import { ensureArray, errorParser } from '@/helpers'
import { useAuthStore, useNotificationStore, useWorkerStore } from '@/stores'

export default defineComponent({
  components: {
    Modal,
  },
  emits: {
    'worker-created': () => true,
  },
  data: () => ({
    openModal: false,
    payload: {
      name: '',
      slug: '',
      type: '',
    } as CreateWorkerPayload,
    loading: false,
    fieldErrors: {} as Partial<Record<keyof CreateWorkerPayload, string[]>>,
  }),
  computed: {
    ...mapState(useAuthStore, ['user']),
    ...mapState(useWorkerStore, ['workerTypes']),
    canCreate(): boolean {
      return (
        this.user !== false &&
        this.user !== null &&
        (this.user.is_admin || this.user.can_create_worker_version)
      )
    },
    canSave(): boolean {
      return this.canCreate && !Object.values(this.payload).some((v) => v === '')
    },
    saveTitle(): string {
      return this.canSave ? 'Create a new worker' : 'All fields are required'
    },
  },
  methods: {
    ...mapActions(useWorkerStore, ['createWorker', 'listWorkerTypes']),
    ...mapActions(useNotificationStore, ['notify']),
    async create() {
      if (!this.canSave) return
      this.loading = true
      this.fieldErrors = {}
      try {
        await this.createWorker(this.payload)
        // Send a custom event to the parent component so that it reloads the list of workers
        this.$emit('worker-created')
        // Reload the list of worker types (for the type filter) if the worker was created with a new type
        if (!Object.values(this.workerTypes).some((type) => type.slug === this.payload.type)) {
          this.listWorkerTypes()
        }
        this.notify({ type: 'info', text: 'Worker created successfully.' })
        this.openModal = false
        this.payload = {
          name: '',
          slug: '',
          type: '',
        }
      } catch (err) {
        if (err instanceof DuplicatedWorker) {
          this.fieldErrors.slug = [`A worker with slug "${this.payload.slug}" already exists`]
        } else {
          this.notify({ type: 'error', text: errorParser(err) })
          if (isAxiosError(err)) {
            this.fieldErrors = err?.response?.data
              ? Object.fromEntries(
                  Object.entries(err?.response?.data).map(([k, v]) => [k, ensureArray(v)]),
                )
              : {}
          }
        }
      } finally {
        this.loading = false
      }
    },
  },
})
</script>
